import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FiMessageCircle,
  FiPaperclip,
  FiSend,
  FiX,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import api from "../../services/api";
import {
  readSupportAttachment,
  SupportAttachmentPreview,
  SupportAvatar,
} from "./SupportChatParts";
import "../../styles/support-chat.css";

const FAQS = [
  {
    id: "orders",
    question: "How do I place an order?",
    answer:
      "Browse the shop, add the items you need to your cart, then follow the checkout steps. The available payment and delivery options are shown before you confirm your order.",
  },
  {
    id: "payments",
    question: "Which payment methods can I use?",
    answer:
      "Payment methods available for this store are shown during checkout. For your security, never send card or banking details through chat.",
  },
  {
    id: "delivery",
    question: "How does delivery work?",
    answer:
      "Delivery options and charges are shown during checkout. Your order confirmation and dispatch updates contain the latest information for your order.",
  },
  {
    id: "tracking",
    question: "How can I track an order?",
    answer:
      "Open the Track Order page and enter the order details requested there. You can also contact the store if you need help locating an order.",
    link: { href: "/track-order", label: "Track an order" },
  },
  {
    id: "returns",
    question: "What is the returns policy?",
    answer:
      "Please review this store's returns policy for eligibility and time limits. If your situation is not covered there, send the store a message and include your order number.",
    link: { href: "/returns", label: "Read the returns policy" },
  },
];

const getInitialContact = (user) => ({
  customerName: user?.name || user?.userName || "",
  customerEmail: user?.email || "",
});

const SupportChatWidget = () => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const location = useLocation();
  const isProductDetail = /^\/product\/[^/]+/.test(location.pathname);
  const ticketStorageKey = `support-active-ticket:${tenant?.TenantCode || "store"}`;
  const [restoredTicket] = useState(() => {
    try {
      const storedTicket = sessionStorage.getItem(ticketStorageKey);
      return storedTicket ? JSON.parse(storedTicket) : null;
    } catch (error) {
      console.warn("Could not restore support chat session:", error);
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(Boolean(restoredTicket?.TicketID));
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [contact, setContact] = useState(() => getInitialContact(user));
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(
    restoredTicket?.TicketID || null,
  );
  const [notificationSent, setNotificationSent] = useState(true);
  const [submissionError, setSubmissionError] = useState("");
  const [activeTicket, setActiveTicket] = useState(restoredTicket);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [conversationError, setConversationError] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [ratingError, setRatingError] = useState("");
  const typingTimerRef = useRef(null);
  const typingActiveRef = useRef(false);
  const conversationTicketIdRef = useRef("");
  const lastMessageIdRef = useRef("");
  const messagesContainerRef = useRef(null);

  useEffect(
    () => () => window.clearTimeout(typingTimerRef.current),
    [],
  );

  useEffect(() => {
    try {
      if (activeTicket) {
        sessionStorage.setItem(ticketStorageKey, JSON.stringify(activeTicket));
      } else {
        sessionStorage.removeItem(ticketStorageKey);
      }
    } catch (error) {
      console.warn("Could not persist support chat session:", error);
    }
  }, [activeTicket, ticketStorageKey]);

  const fetchConversation = useCallback(async () => {
    if (!activeTicket?.TicketID) return;
    const ticketId = activeTicket.TicketID;
    if (conversationTicketIdRef.current !== ticketId) {
      conversationTicketIdRef.current = ticketId;
      lastMessageIdRef.current = "";
      setConversationMessages([]);
    }
    const afterMessageId = lastMessageIdRef.current;
    try {
      const response = await api.get(
        `/api/support/tickets/${ticketId}/messages`,
        {
          headers: activeTicket.AccessKey
            ? { "X-Support-Access-Key": activeTicket.AccessKey }
            : {},
          params: afterMessageId ? { afterMessageId } : {},
        },
      );
      if (
        conversationTicketIdRef.current !== ticketId ||
        (afterMessageId && lastMessageIdRef.current !== afterMessageId)
      ) {
        return;
      }
      const newMessages = response.data.messages || [];
      setConversationMessages((current) =>
        afterMessageId ? [...current, ...newMessages] : newMessages,
      );
      if (response.data.ticket) {
        setActiveTicket((current) => {
          const refreshedTicket = response.data.ticket;
          if (
            current?.Status === refreshedTicket.Status &&
            (current?.CustomerRating ?? null) === refreshedTicket.CustomerRating &&
            (current?.CustomerRatingComment ?? null) === refreshedTicket.CustomerRatingComment
          ) {
            return current;
          }
          return { ...current, ...refreshedTicket };
        });
      }
      if (newMessages.length) {
        lastMessageIdRef.current = newMessages[newMessages.length - 1].MessageID;
      }
      setIsOtherTyping(Boolean(response.data.isOtherTyping));
      setConversationError("");
    } catch (error) {
      console.error("Could not refresh support conversation:", error);
      setConversationError(
        error.response?.data?.message ||
          "Could not refresh this conversation. Please try again.",
      );
    }
  }, [activeTicket]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [conversationMessages.length, isOtherTyping]);

  const updateTypingIndicator = useCallback(
    async (isTyping) => {
      if (!activeTicket?.TicketID) return;
      try {
        await api.post(
          `/api/support/tickets/${activeTicket.TicketID}/typing`,
          { isTyping },
          {
            headers: activeTicket.AccessKey
              ? { "X-Support-Access-Key": activeTicket.AccessKey }
              : {},
          },
        );
      } catch (error) {
        console.warn("Could not update support typing indicator:", error);
      }
    },
    [activeTicket],
  );

  const handleChatDraftChange = (event) => {
    const value = event.target.value;
    setChatDraft(value);
    window.clearTimeout(typingTimerRef.current);
    if (value.trim()) {
      typingTimerRef.current = window.setTimeout(
        () => {
          typingActiveRef.current = true;
          updateTypingIndicator(true);
        },
        450,
      );
    } else {
      stopTyping();
    }
  };

  const stopTyping = () => {
    window.clearTimeout(typingTimerRef.current);
    if (!typingActiveRef.current) return;
    typingActiveRef.current = false;
    updateTypingIndicator(false);
  };

  const chooseAttachment = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setAttachment(await readSupportAttachment(file));
      setConversationError("");
    } catch (error) {
      setConversationError(error.message);
    }
  };

  useEffect(() => {
    if (!activeTicket?.TicketID) return undefined;
    fetchConversation();
    const interval = window.setInterval(fetchConversation, 3000);
    return () => window.clearInterval(interval);
  }, [activeTicket?.TicketID, fetchConversation]);

  const startContact = () => {
    setShowForm(true);
    setSelectedFaq(null);
    setSubmittedTicket(null);
    setContact(getInitialContact(user));
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmissionError("");
    try {
      const response = await api.post("/api/support/tickets", {
        ...contact,
        subject,
        message,
      });
      setSubmittedTicket(
        response.data.ticket?.ReferenceCode ||
          response.data.ticket?.TicketID ||
          "received",
      );
      setNotificationSent(response.data.notificationSent !== false);
      setActiveTicket(response.data.ticket || null);
      setConversationMessages([]);
      conversationTicketIdRef.current = "";
      lastMessageIdRef.current = "";
      setMessage("");
    } catch (error) {
      console.error("Could not submit support request:", error);
      setSubmissionError(
        error.response?.data?.message ||
          "We could not send your request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetConversation = () => {
    stopTyping();
    setSelectedFaq(null);
    setShowForm(false);
    setSubmittedTicket(null);
    setSubject("");
    setMessage("");
    setActiveTicket(null);
    setConversationMessages([]);
    setChatDraft("");
    setAttachment(null);
    setConversationError("");
    setIsOtherTyping(false);
    setRating(0);
    setRatingComment("");
    setRatingError("");
  };

  const submitRating = async (event) => {
    event.preventDefault();
    if (!activeTicket?.TicketID || rating < 1 || isSubmittingRating) return;
    setIsSubmittingRating(true);
    setRatingError("");
    try {
      const response = await api.post(
        `/api/support/tickets/${activeTicket.TicketID}/rating`,
        { rating, comment: ratingComment },
        {
          headers: activeTicket.AccessKey
            ? { "X-Support-Access-Key": activeTicket.AccessKey }
            : {},
        },
      );
      setActiveTicket((current) => ({ ...current, ...response.data.rating }));
    } catch (error) {
      setRatingError(
        error.response?.data?.message || "We could not save your rating. Please try again.",
      );
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const sendChatMessage = async (event) => {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!activeTicket?.TicketID || (!text && !attachment) || isSendingMessage) return;

    stopTyping();
    setIsSendingMessage(true);
    try {
      await api.post(
        `/api/support/tickets/${activeTicket.TicketID}/messages`,
        { message: text, attachment },
        {
          headers: activeTicket.AccessKey
            ? { "X-Support-Access-Key": activeTicket.AccessKey }
            : {},
        },
      );
      setChatDraft("");
      setAttachment(null);
      await fetchConversation();
    } catch (error) {
      console.error("Could not send support message:", error);
      setConversationError(
        error.response?.data?.message || "Your message could not be sent.",
      );
    } finally {
      setIsSendingMessage(false);
    }
  };

  return createPortal(
    <div
      className={`support-chat-widget${isProductDetail ? " is-product-detail" : ""}`}
    >
      {isOpen && (
        <section
          className="support-chat-panel"
          role="dialog"
          aria-label="Store customer support"
        >
          <header className="support-chat-header">
            <div>
              <span className="support-chat-status" />
              <strong>{tenant?.BusinessName || "Customer support"}</strong>
              <small>Store support · we’re here to help</small>
            </div>
            <button
              type="button"
              className="support-chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close customer support"
            >
              <FiX />
            </button>
          </header>

          <div className="support-chat-content">
            {activeTicket ? (
              <div className="support-chat-live">
                <div className="support-chat-ticket-identity">
                  <strong>{activeTicket.BusinessName || "Store"} support</strong>
                  <span>Reference: {activeTicket.ReferenceCode || activeTicket.TicketID}</span>
                  <small>
                    Messages are encrypted in storage and refresh automatically.
                  </small>
                </div>
                {!notificationSent && (
                  <p className="support-chat-warning">
                    The request was saved, but the store email notification could not be sent.
                  </p>
                )}
                {conversationError && (
                  <p className="support-chat-form-error" role="alert">
                    {conversationError}
                  </p>
                )}
                <div
                  className="support-chat-message-list"
                  aria-live="polite"
                  ref={messagesContainerRef}
                >
                  {conversationMessages.map((entry) => (
                    <div
                      className={`support-chat-message-row ${
                        entry.SenderType === "Customer" ? "from-customer" : "from-store"
                      }`}
                      key={entry.MessageID}
                    >
                      <SupportAvatar
                        emoji={entry.SenderType === "Customer" ? "👤" : "🧑‍💼"}
                      />
                      <article className="support-chat-message-bubble">
                        <span>{entry.SenderName}</span>
                        {entry.Message && <p>{entry.Message}</p>}
                        <SupportAttachmentPreview attachment={entry.Attachment} />
                        <time>{new Date(entry.CreatedAt).toLocaleTimeString()}</time>
                      </article>
                    </div>
                  ))}
                  {isOtherTyping && (
                    <div className="support-chat-typing" role="status" aria-label="Consultant is typing">
                      <span className="support-typing-bubble" aria-hidden="true">
                        <i /><i /><i />
                      </span>
                    </div>
                  )}
                  {conversationMessages.length === 0 && (
                    <p className="support-chat-waiting">
                      Your request is with the store. You can add more details here.
                    </p>
                  )}
                </div>
                {["Resolved", "Closed"].includes(activeTicket.Status) ? (
                  <section className="support-chat-rating">
                    {activeTicket.CustomerRating ? (
                      <p role="status">Thanks for rating your support experience!</p>
                    ) : (
                      <form onSubmit={submitRating}>
                        <strong>This conversation has ended. How did we do?</strong>
                        <div className="support-chat-rating-stars" role="radiogroup" aria-label="Rate support from 1 to 5 stars">
                          {[1, 2, 3, 4, 5].map((value) => (
                            <button
                              type="button"
                              key={value}
                              role="radio"
                              aria-checked={rating === value}
                              aria-label={`${value} star${value === 1 ? "" : "s"}`}
                              className={rating >= value ? "selected" : ""}
                              onClick={() => setRating(value)}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        <textarea
                          value={ratingComment}
                          maxLength={500}
                          onChange={(event) => setRatingComment(event.target.value)}
                          placeholder="Optional feedback"
                          aria-label="Optional feedback"
                        />
                        {ratingError && <p className="support-chat-form-error" role="alert">{ratingError}</p>}
                        <button type="submit" disabled={rating < 1 || isSubmittingRating}>
                          {isSubmittingRating ? "Submitting…" : "Submit rating"}
                        </button>
                      </form>
                    )}
                  </section>
                ) : (
                <form className="support-chat-live-form" onSubmit={sendChatMessage}>
                  <label className="support-chat-attach" aria-label="Attach a file">
                    <FiPaperclip />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={chooseAttachment}
                      aria-label="Attach JPG, PNG, WebP, or PDF"
                    />
                  </label>
                  <textarea
                    value={chatDraft}
                    maxLength={4000}
                    rows={2}
                    onChange={handleChatDraftChange}
                    onBlur={stopTyping}
                    placeholder="Write a message…"
                    aria-label="Message the store"
                  />
                  <button
                    type="submit"
                    disabled={isSendingMessage || (!chatDraft.trim() && !attachment)}
                    aria-label="Send message"
                  >
                    <FiSend />
                  </button>
                </form>
                )}
                {attachment && (
                  <div className="support-chat-selected-attachment">
                    <span>{attachment.name} · {(attachment.size / 1024).toFixed(0)} KB</span>
                    <button
                      type="button"
                      onClick={() => setAttachment(null)}
                      aria-label="Remove attachment"
                    >
                      <FiX />
                    </button>
                  </div>
                )}
                {(!["Resolved", "Closed"].includes(activeTicket.Status) ||
                  activeTicket.CustomerRating) && (
                  <button
                    type="button"
                    className="support-chat-new-request"
                    onClick={resetConversation}
                  >
                    Start another request
                  </button>
                )}
              </div>
            ) : submittedTicket ? (
              <div className="support-chat-success" role="status">
                <strong>Your request is in the support queue.</strong>
                <p>Reference: {submittedTicket}</p>
                {!notificationSent && (
                  <p className="support-chat-warning">
                    The request was saved, but the store email notification could not be sent.
                  </p>
                )}
                <button type="button" onClick={resetConversation}>
                  Start another conversation
                </button>
              </div>
            ) : showForm ? (
              <>
                <p className="support-chat-intro">
                  {user
                    ? "Send a message to the store. We’ll use the contact details on your account."
                    : "Leave your contact details and message. The store will follow up by email."}
                </p>
                <form className="support-chat-form" onSubmit={submitTicket}>
                  {submissionError && (
                    <p className="support-chat-form-error" role="alert">
                      {submissionError}
                    </p>
                  )}
                  {!user && (
                    <>
                      <label>
                        Your name
                        <input
                          required
                          maxLength={120}
                          value={contact.customerName}
                          onChange={(event) =>
                            setContact({ ...contact, customerName: event.target.value })
                          }
                          autoComplete="name"
                        />
                      </label>
                      <label>
                        Email address
                        <input
                          required
                          type="email"
                          maxLength={254}
                          value={contact.customerEmail}
                          onChange={(event) =>
                            setContact({ ...contact, customerEmail: event.target.value })
                          }
                          autoComplete="email"
                        />
                      </label>
                    </>
                  )}
                  <label>
                    Subject
                    <input
                      required
                      maxLength={160}
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      placeholder="What do you need help with?"
                    />
                  </label>
                  <label>
                    Message
                    <textarea
                      required
                      maxLength={4000}
                      rows={4}
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="Share a few details. Do not include payment information."
                    />
                  </label>
                  <div className="support-chat-form-actions">
                    <button
                      type="button"
                      className="support-chat-back"
                      onClick={() => setShowForm(false)}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="support-chat-submit"
                      disabled={isSubmitting}
                    >
                      <FiSend />
                      {isSubmitting ? "Sending..." : "Send request"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <p className="support-chat-intro">
                  Hi{user?.name || user?.userName ? ` ${user.name || user.userName}` : ""}! Choose a question or contact the store team.
                </p>
                {selectedFaq ? (
                  <div className="support-chat-answer">
                    <strong>{selectedFaq.question}</strong>
                    <p>{selectedFaq.answer}</p>
                    {selectedFaq.link && (
                      <a href={selectedFaq.link.href}>{selectedFaq.link.label}</a>
                    )}
                    <button type="button" onClick={() => setSelectedFaq(null)}>
                      Browse common questions
                    </button>
                  </div>
                ) : (
                  <div className="support-chat-faqs">
                    {FAQS.map((faq) => (
                      <button
                        type="button"
                        key={faq.id}
                        onClick={() => setSelectedFaq(faq)}
                      >
                        {faq.question}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="support-chat-consultant"
                  onClick={startContact}
                >
                  Contact a consultant
                </button>
              </>
            )}
          </div>
        </section>
      )}
      <button
        type="button"
        className={`support-chat-launcher ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? "Close customer support" : "Open customer support"}
        aria-expanded={isOpen}
        title={isOpen ? "Close customer support" : "Open customer support"}
      >
        {isOpen ? <FiX /> : <><FiMessageCircle /><span>Need help?</span></>}
      </button>
    </div>,
    document.body,
  );
};

export default SupportChatWidget;
